<template>
  <section class="app-table-wrap suppliers-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">{{ text.table.headers.index }}</th>
            <th>
              <button
                type="button"
                class="suppliers-table-sort-btn"
                :title="text.actions.table.sortBySupplier"
                @click="toggleSort('supplier')"
              >
                <span>{{ text.table.headers.supplier }}</span>
                <i class="fa" :class="sortIconClass('supplier')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.table.headers.category }}</th>
            <th>{{ text.table.headers.mainContact }}</th>
            <th>{{ text.table.headers.email }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="suppliers-table-sort-btn suppliers-table-sort-btn--right"
                :title="text.actions.table.sortByPendingExpenses"
                @click="toggleSort('pendingExpenses')"
              >
                <span>{{ text.table.headers.pendingExpenses }}</span>
                <i class="fa" :class="sortIconClass('pendingExpenses')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">
              <button
                type="button"
                class="suppliers-table-sort-btn suppliers-table-sort-btn--right"
                :title="text.actions.table.sortByPendingTotal"
                @click="toggleSort('pendingTotal')"
              >
                <span>{{ text.table.headers.pendingTotal }}</span>
                <i class="fa" :class="sortIconClass('pendingTotal')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">
              <button
                type="button"
                class="suppliers-table-sort-btn suppliers-table-sort-btn--right"
                :title="text.actions.table.sortByPaidTotal"
                @click="toggleSort('paidTotal')"
              >
                <span>{{ text.table.headers.paidTotal }}</span>
                <i class="fa" :class="sortIconClass('paidTotal')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.table.headers.status }}</th>
            <th v-if="showActionsColumn"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="visibleColumnsCount" class="app-table-empty">{{ text.table.loading }}</td>
          </tr>

          <tr v-else-if="rows.length === 0">
            <td :colspan="visibleColumnsCount" class="app-table-empty">{{ emptyText }}</td>
          </tr>

          <tr v-else v-for="(row, index) in sortedRows" :key="row.id">
            <td class="app-muted">{{ rowNumber(index) }}</td>
            <td class="app-strong">
              <a
                v-if="canViewDetail"
                :href="resolveSupplierHref(row)"
                class="suppliers-table-link"
                :title="text.actions.table.goSupplierDetail"
                @click="navigateToSupplierDetail(row, $event)"
              >
                {{ row.name || '' }}
              </a>
              <span v-else>{{ row.name || '' }}</span>
            </td>
            <td>
              <div class="suppliers-cell-stack">
                <strong class="suppliers-cell-main">{{ resolveClassName(row) }}</strong>
                <span class="suppliers-cell-sub">{{ resolveCategoryName(row) }}</span>
              </div>
            </td>
            <td>
              <span v-if="row.main_contact_name">{{ row.main_contact_name }}</span>
              <span v-else-if="row.main_contact && row.main_contact.name">{{ row.main_contact.name }}</span>
              <span v-else class="app-muted"></span>
            </td>
            <td>
              <span v-if="row.email">{{ row.email }}</span>
              <span v-else class="app-muted"></span>
            </td>
            <td class="app-text-right app-strong">
              <button
                v-if="resolvePendingCountTotal(row) > 0"
                type="button"
                class="suppliers-table-link suppliers-table-link--plain suppliers-table-link--pending"
                :title="text.actions.table.openPendingBreakdown"
                @click="handlePendingExpensesClick(row, $event)"
              >
                {{ formatInteger(resolvePendingCountTotal(row)) }}
              </button>
              <span v-else>{{ formatInteger(0) }}</span>
            </td>
            <td class="app-text-right app-strong">{{ formatCurrency(resolvePendingTotalAmount(row)) }}</td>
            <td class="app-text-right app-strong">{{ formatCurrency(resolvePaidTotalAmount(row)) }}</td>
            <td>
              <span class="app-status-pill" :class="row.active ? 'app-status-active' : 'app-status-inactive'">
                {{ row.active ? text.table.status.active : text.table.status.inactive }}
              </span>
            </td>
            <td v-if="showActionsColumn" class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  v-if="canViewDetail"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--view"
                  :title="text.actions.table.viewDetail"
                  @click="navigateToSupplierDetail(row)"
                >
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </button>

                <button
                  v-if="canEdit"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--edit"
                  :title="text.actions.table.editSupplier"
                  @click="emitEdit(row)"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>

                <div v-if="canEdit || canDelete" class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="text.actions.common.moreActions"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="normalizedPagination.total_pages > 1" class="app-table-footer">
      <AppPagination
        :current-page="normalizedPagination.current_page"
        :total-pages="normalizedPagination.total_pages"
        @page-change="requestPage"
      />
    </div>

    <teleport to="body">
      <div
        v-if="openRow"
        class="app-table-actions-menu app-table-floating-menu"
        :style="menuStyle"
      >
        <button
          v-if="canEdit"
          type="button"
          @click="emitAction('toggle-status', openRow)"
        >
          {{ openRow.active ? text.actions.common.disable : text.actions.common.activate }}
        </button>
        <button
          v-if="canDelete"
          type="button"
          class="app-table-actions-menu-btn--danger"
          @click="emitAction('delete', openRow)"
        >
          {{ text.actions.common.delete }}
        </button>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="pendingBreakdownRow"
        class="app-table-actions-menu app-table-floating-menu suppliers-pending-breakdown-menu"
        :style="pendingBreakdownStyle"
      >
        <div class="suppliers-pending-breakdown-menu__title">Selecciona un tipo de egreso</div>
        <button
          v-if="resolvePendingExpensesCount(pendingBreakdownRow) > 0"
          type="button"
          class="suppliers-pending-breakdown-menu__entry"
          @click="goToExpensesWithSupplier(pendingBreakdownRow)"
        >
          {{ text.actions.table.pendingExpensesEntry(resolvePendingExpensesCount(pendingBreakdownRow)) }}
        </button>
        <button
          v-if="resolvePendingFeesCount(pendingBreakdownRow) > 0"
          type="button"
          class="suppliers-pending-breakdown-menu__entry"
          @click="goToFeesWithSupplier(pendingBreakdownRow)"
        >
          {{ text.actions.table.pendingFeesEntry(resolvePendingFeesCount(pendingBreakdownRow)) }}
        </button>
      </div>
    </teleport>
  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import { ensureValidPage, normalizePaginationPayload } from '@/composables/shared/usePagination';
import {
  resolveSortIconClass,
  resolveSortTogglePayload,
  sortRows,
} from '@/composables/shared/useSorting';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import { formatCurrency as formatCurrencyUtil } from '@/utils/numberFormat';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');

const SORTABLE_KEYS = ['supplier', 'pendingExpenses', 'pendingTotal', 'paidTotal'];
const SORT_VALUE_RESOLVERS = {
  supplier: (row) => row?.name ?? '',
  pendingExpenses: (row) => Number(row?.pending_expenses_count ?? row?.expenses_total ?? 0) + Number(row?.pending_fees_count ?? 0),
  pendingTotal: (row) => Number(row?.current_year_pending_expenses_total ?? row?.current_year_total_expenses ?? 0) + Number(row?.current_year_pending_fees_total ?? 0),
  paidTotal: (row) => Number(row?.current_year_paid_expenses ?? 0) + Number(row?.current_year_paid_fees ?? 0),
};

export default {
  name: 'SuppliersTable',
  components: {
    AppPagination,
  },
  emits: ['page-change', 'delete-supplier', 'toggle-supplier-status', 'edit-supplier'],
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      }),
    },
    emptyText: {
      type: String,
      default: text.table.empty,
    },
    capabilities: {
      type: Object,
      default: () => ({
        canViewDetail: true,
        canDelete: true,
        canEdit: true,
      }),
    },
  },
  data() {
    return {
      text,
      sortBy: '',
      sortDirection: 'asc',
      activeMenuSupplierId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
      pendingBreakdownSupplierId: null,
      pendingBreakdownTop: 0,
      pendingBreakdownLeft: 0,
    };
  },
  computed: {
    normalizedPagination() {
      return normalizePaginationPayload(this.pagination, {
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      });
    },
    canViewDetail() {
      return Boolean(this.capabilities?.canViewDetail);
    },
    canDelete() {
      return Boolean(this.capabilities?.canDelete);
    },
    canEdit() {
      return Boolean(this.capabilities?.canEdit);
    },
    showActionsColumn() {
      return this.canViewDetail || this.canDelete || this.canEdit;
    },
    visibleColumnsCount() {
      let count = 9;

      if (this.showActionsColumn) {
        count += 1;
      }

      return count;
    },
    sortedRows() {
      return sortRows(
        this.rows,
        {
          field: this.sortBy,
          direction: this.sortDirection,
        },
        SORT_VALUE_RESOLVERS,
      );
    },
    openRow() {
      if (!this.activeMenuSupplierId) {
        return null;
      }

      return this.rows.find((row) => Number(row?.id || 0) === Number(this.activeMenuSupplierId)) || null;
    },
    menuStyle() {
      return {
        position: 'fixed',
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
        zIndex: 1300,
      };
    },
    pendingBreakdownRow() {
      if (!this.pendingBreakdownSupplierId) {
        return null;
      }

      return this.rows.find((row) => Number(row?.id || 0) === Number(this.pendingBreakdownSupplierId)) || null;
    },
    pendingBreakdownStyle() {
      return {
        position: 'fixed',
        top: `${this.pendingBreakdownTop}px`,
        left: `${this.pendingBreakdownLeft}px`,
        zIndex: 1300,
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick, true);
    window.addEventListener('resize', this.closeOpenMenus);
    window.addEventListener('scroll', this.closeOpenMenus, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
    window.removeEventListener('resize', this.closeOpenMenus);
    window.removeEventListener('scroll', this.closeOpenMenus, true);
  },
  methods: {
    sortIconClass(field) {
      return resolveSortIconClass(
        {
          field: this.sortBy,
          direction: this.sortDirection,
        },
        field,
      );
    },
    toggleSort(key) {
      if (!SORTABLE_KEYS.includes(key)) {
        return;
      }

      const nextPayload = resolveSortTogglePayload(
        {
          field: this.sortBy,
          direction: this.sortDirection,
        },
        key,
        SORTABLE_KEYS,
      );

      if (!nextPayload) {
        return;
      }

      this.sortBy = nextPayload.field;
      this.sortDirection = nextPayload.direction;
    },
    toggleActionsMenu(supplierId, event) {
      this.closePendingBreakdownMenu();

      if (!event?.currentTarget) {
        return;
      }

      const nextId = resolveToggledMenuId(this.activeMenuSupplierId, supplierId);
      this.activeMenuSupplierId = nextId;

      if (!nextId) {
        return;
      }

      const position = computeFloatingMenuPosition(event.currentTarget, 220, 6, 8);
      this.floatingMenuTop = position.top;
      this.floatingMenuLeft = position.left;
    },
    formatCurrency(value) {
      return formatCurrencyUtil(value);
    },
    formatInteger(value) {
      const parsed = Number(value || 0);
      const safeValue = Number.isFinite(parsed) ? parsed : 0;

      return new Intl.NumberFormat('es-MX', {
        maximumFractionDigits: 0,
      }).format(safeValue);
    },
    resolveClassName(row) {
      return String(row?.class?.name || row?.class_name || '').trim();
    },
    resolveCategoryName(row) {
      return String(row?.category?.name || row?.category_name || '').trim();
    },
    resolveSupplierHref(row) {
      return resolveRouteHref(this.$router, 'supplierDetail', 'supplierId', row?.id);
    },
    resolvePendingExpensesCount(row) {
      return Number(row?.pending_expenses_count ?? row?.expenses_total ?? 0) || 0;
    },
    resolvePendingFeesCount(row) {
      return Number(row?.pending_fees_count ?? 0) || 0;
    },
    resolvePendingCountTotal(row) {
      return this.resolvePendingExpensesCount(row) + this.resolvePendingFeesCount(row);
    },
    resolvePendingTotalAmount(row) {
      return Number(row?.current_year_pending_expenses_total ?? row?.current_year_total_expenses ?? 0)
        + Number(row?.current_year_pending_fees_total ?? 0);
    },
    resolvePaidTotalAmount(row) {
      return Number(row?.current_year_paid_expenses ?? 0)
        + Number(row?.current_year_paid_fees ?? 0);
    },
    hasOnlyPendingExpenses(row) {
      return this.resolvePendingExpensesCount(row) > 0 && this.resolvePendingFeesCount(row) <= 0;
    },
    hasOnlyPendingFees(row) {
      return this.resolvePendingFeesCount(row) > 0 && this.resolvePendingExpensesCount(row) <= 0;
    },
    resolveSupplierFilterQuery(row) {
      const supplierId = Number(row?.id || 0);
      const supplierName = String(row?.name || '').trim();

      return {
        supplier_id: supplierId > 0 ? String(supplierId) : '',
        supplier_name: supplierName,
      };
    },
    goToExpensesWithSupplier(row) {
      this.closePendingBreakdownMenu();
      this.$router.push({ name: 'expenses', query: this.resolveSupplierFilterQuery(row) });
    },
    goToFeesWithSupplier(row) {
      this.closePendingBreakdownMenu();
      this.$router.push({ name: 'fees', query: this.resolveSupplierFilterQuery(row) });
    },
    handlePendingExpensesClick(row, event) {
      if (!event?.currentTarget) {
        return;
      }

      this.closeActionsMenu();

      if (this.resolvePendingCountTotal(row) <= 0) {
        return;
      }

      if (this.hasOnlyPendingExpenses(row)) {
        this.goToExpensesWithSupplier(row);
        return;
      }

      if (this.hasOnlyPendingFees(row)) {
        this.goToFeesWithSupplier(row);
        return;
      }

      const nextId = resolveToggledMenuId(this.pendingBreakdownSupplierId, row?.id);
      this.pendingBreakdownSupplierId = nextId;

      if (!nextId) {
        return;
      }

      const position = computeFloatingMenuPosition(event.currentTarget, 240, 6, 8);
      this.pendingBreakdownTop = position.top;
      this.pendingBreakdownLeft = position.left;
    },
    navigateToSupplierDetail(row, event = null) {
      if (!this.canViewDetail) {
        return;
      }

      navigateToRouteDetail(this.$router, 'supplierDetail', 'supplierId', row?.id, event);
    },
    emitDelete(row) {
      this.$emit('delete-supplier', row);
    },
    emitToggleStatus(row) {
      this.$emit('toggle-supplier-status', row);
    },
    emitEdit(row) {
      this.$emit('edit-supplier', row);
    },
    closeActionsMenu() {
      this.activeMenuSupplierId = null;
    },
    closePendingBreakdownMenu() {
      this.pendingBreakdownSupplierId = null;
    },
    closeOpenMenus() {
      this.closeActionsMenu();
      this.closePendingBreakdownMenu();
    },
    handleGlobalClick(event) {
      if (!this.activeMenuSupplierId && !this.pendingBreakdownSupplierId) {
        return;
      }

      const target = event?.target;
      if (target instanceof Element && target.closest('.app-table-floating-menu')) {
        return;
      }

      if (target instanceof Element && target.closest('.app-table-actions-menu-wrap')) {
        return;
      }

      if (target instanceof Element && target.closest('.suppliers-table-link--pending')) {
        return;
      }

      this.closeActionsMenu();
      this.closePendingBreakdownMenu();
    },
    emitAction(action, row) {
      this.closeActionsMenu();

      if (action === 'toggle-status') {
        this.emitToggleStatus(row);
        return;
      }

      if (action === 'delete') {
        this.emitDelete(row);
      }
    },
    rowNumber(index) {
      const currentPage = Number(this.normalizedPagination.current_page || 1);
      const perPage = Number(this.normalizedPagination.per_page || 20);

      return ((currentPage - 1) * perPage) + Number(index) + 1;
    },
    requestPage(page) {
      const nextPage = Number(page || 1);
      if (!Number.isFinite(nextPage)) {
        return;
      }

      const validPage = ensureValidPage(nextPage, this.normalizedPagination.total_pages);

      if (validPage === this.normalizedPagination.current_page) {
        return;
      }

      this.$emit('page-change', validPage);
    },
  },
};
</script>

<style scoped>
.suppliers-table-wrap {
  margin-top: 14px;
}

.suppliers-table-link {
  color: #0070cd;
  text-decoration: none;
  cursor: pointer;
}

.suppliers-table-link:hover {
  text-decoration: underline;
}

.suppliers-table-link--plain,
.suppliers-table-link--plain:hover {
  text-decoration: none;
}

.suppliers-table-link--pending {
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  text-align: right;
}

.suppliers-pending-breakdown-menu {
  min-width: 240px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid rgba(0, 112, 205, 0.2);
  background: linear-gradient(180deg, #ffffff 0%, #f6fbff 100%);
  box-shadow: 0 10px 28px rgba(2, 60, 112, 0.16);
}

.suppliers-pending-breakdown-menu__title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0b4c87;
  font-weight: 700;
  margin-bottom: 8px;
}

.suppliers-pending-breakdown-menu__entry {
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: #eaf4ff;
  color: #0b4c87;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
  padding: 8px 10px;
}

.suppliers-pending-breakdown-menu__entry + .suppliers-pending-breakdown-menu__entry {
  margin-top: 6px;
}

.suppliers-pending-breakdown-menu__entry:hover {
  background: #d9ebff;
}

.suppliers-table-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.suppliers-table-sort-btn--right {
  width: 100%;
  justify-content: flex-end;
}

.suppliers-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.suppliers-cell-main {
  font-weight: 700;
  line-height: 1.2;
  color: #1f2937;
}

.suppliers-cell-sub {
  font-size: 12px;
  line-height: 1.2;
  color: #4b5563;
}
</style>
